import { Modal } from '@sgcmpteltd/uikit-next';
import { Button, Select } from '@sgcmpteltd/uikit-react';
import {
  BUTTON_COLOR_RED,
  BUTTON_VARIANT_CONTAINED,
} from '@sgcmpteltd/uikit-react/components/button';
import type { SelectOption } from '@sgcmpteltd/uikit-react/components/select';
import cx from 'clsx';
import { useState } from 'react';

import { ACTION_STATUS_SUCCESS } from '@/actions';
import { getCarDetailData } from '@/actions/new-car/car-detail';
import { getCarModelsListingData } from '@/actions/new-car/car-models';

import styles from './styles.module.scss';
import type { Component } from './types';

export const CompareModal: Component = ({
  isOpen,
  brandList,
  onClose,
  onSubmit,
}) => {
  const [selectedMake, setSelectedMake] = useState('');
  const [
    modelSelectList,
    setModelSelectList,
  ] = useState<SelectOption[]>([]);
  const [selectedModel, setSelectedModel] = useState(-1);
  const [
    submodelSelectList,
    setSubmodelSelectList,
  ] = useState<SelectOption[]>([]);
  const [selectedSubmodel, setSelectedSubmodel] = useState(-1);

  const handleClose = () => {
    setSelectedMake('');
    setModelSelectList([]);
    setSelectedModel(-1);
    setSubmodelSelectList([]);
    setSelectedSubmodel(-1);
    onClose?.();
  };

  const handleMakeChange = async (value: string | number) => {
    if (typeof value !== 'string') {
      return;
    }
    setSelectedMake(value);

    const formData = new FormData();

    formData.set('make', value);

    const actionResult = await getCarModelsListingData(formData);

    if (actionResult.status === ACTION_STATUS_SUCCESS) {
      setSelectedModel(-1);
      setModelSelectList(actionResult.data.data.map(model => ({
        text: model.model,
        value: model.id,
      })).toSorted((a, b) => a.text.localeCompare(b.text)));
      setSelectedSubmodel(-1);
    }
  };

  const handleModelChange = async (value: string | number) => {
    if (typeof value !== 'number') {
      return;
    }
    setSelectedModel(value);

    const formData = new FormData();

    formData.set('id', value.toString());
    const actionResult = await getCarDetailData(formData);

    if (actionResult.status === ACTION_STATUS_SUCCESS) {
      setSubmodelSelectList(actionResult.data.data.subModels.map(submodel => ({
        text: submodel.name,
        value: submodel.id,
      })).toSorted((a, b) => a.text.localeCompare(b.text)));
      setSelectedSubmodel(-1);
    }
  };

  const handleSubmodelChange = async (value: string | number) => {
    if (typeof value !== 'number') {
      return;
    }
    setSelectedSubmodel(value);
  };

  const handleSubmitClick = () => {
    if (!selectedMake || selectedModel === -1 || selectedSubmodel === -1) {
      alert('Please select all fields');
      return;
    }

    onSubmit?.({
      make: selectedMake,
      id: selectedModel,
      submodelId: selectedSubmodel,
    });
    setSelectedMake('');
    setModelSelectList([]);
    setSelectedModel(-1);
    setSubmodelSelectList([]);
    setSelectedSubmodel(-1);
  };

  const handleClearClick = () => {
    setSelectedMake('');
    setModelSelectList([]);
    setSelectedModel(-1);
    setSubmodelSelectList([]);
    setSelectedSubmodel(-1);
  };

  return (
    <Modal
      headerClassName={cx(
        styles.containerModalHeader,
        styles.isCustom,
      )}
      isOpen={isOpen}
      title={null}
      isBackdropStatic
      onClose={handleClose}>
      <div className={styles.containerModalContent}>
        <span className={styles.textTitle}>Select Your Car Make & Model</span>
        <div className={styles.containerSelects}>
          <Select
            menuClassName={cx(
              styles.containerSelctMenu,
              styles.hasMaxHeight,
            )}
            optionList={brandList.map(brand => ({
              text: brand,
              value: brand,
            }))}
            placeholder={{
              text: 'Select Car Make',
              isOptionHidden: true,
            }}
            value={selectedMake}
            onChange={handleMakeChange} />
          <Select
            isDisabled={!selectedMake}
            menuClassName={cx(
              styles.containerSelctMenu,
              styles.hasMaxHeight,
            )}
            optionList={modelSelectList}
            placeholder={{
              text: 'Select Car Model',
              isOptionHidden: true,
            }}
            value={selectedModel}
            onChange={handleModelChange} />
          <Select
            isDisabled={selectedModel === -1}
            menuClassName={cx(
              styles.containerSelctMenu,
              styles.hasMaxHeight,
            )}
            optionList={submodelSelectList}
            placeholder={{
              text: 'Select Submodel',
              isOptionHidden: true,
            }}
            value={selectedSubmodel}
            onChange={handleSubmodelChange} />
        </div>
        <div className={styles.containerBtns}>
          <Button
            className={cx(
              styles.btnSubmit,
              styles.isSmall,
            )}
            color={BUTTON_COLOR_RED}
            variant={BUTTON_VARIANT_CONTAINED}
            onClick={handleSubmitClick}>
            Add To Compare
          </Button>
          <button
            className={styles.btnClear}
            onClick={handleClearClick}>
            Clear
          </button>
        </div>
      </div>
    </Modal>
  );
};
